import type { AIModel } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import type { TranslationKey } from '../i18n/index.js';
import {
  VOICE_NAMES,
  VOICE_REACTIONS,
  VOICE_SPEEDS,
  VOICE_TALK_MODES,
  VOICE_TONES,
  type VoiceSettings,
} from '../lib/voiceSettings.js';

export interface VoiceSettingsPanelProps {
  settings: VoiceSettings;
  onChange: (patch: Partial<VoiceSettings>) => void;
  /** The model the call uses; the web-access switch only shows when it can search. */
  voiceModel: AIModel | undefined;
}

function Segment<T extends string | number>({
  label,
  value,
  options,
  onSelect,
}: {
  label: string;
  value: T;
  options: Array<{ key: T; label: string }>;
  onSelect: (value: T) => void;
}) {
  return (
    <div className="voice-setting">
      <span className="voice-setting-label">{label}</span>
      <div className="mm-seg voice-seg" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={String(option.key)}
            type="button"
            className={`mm-seg-btn${value === option.key ? ' is-active' : ''}`}
            aria-pressed={value === option.key}
            onClick={() => onSelect(option.key)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="voice-toggle">
      <span className="voice-toggle-text">
        <span>{label}</span>
        {hint && <small>{hint}</small>}
      </span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    </label>
  );
}

/** Everything about how the call sounds and reacts; changes apply to the running call. */
export function VoiceSettingsPanel({ settings, onChange, voiceModel }: VoiceSettingsPanelProps) {
  const { t } = useI18n();
  const tr = (key: string) => t(key as TranslationKey);

  return (
    <div className="voice-settings" role="region" aria-label={t('voice.settings')}>
      <label className="voice-setting">
        <span className="voice-setting-label">{t('voice.set.voice')}</span>
        <select
          className="field voice-select"
          value={settings.voice}
          onChange={(event) => onChange({ voice: event.target.value as VoiceSettings['voice'] })}
        >
          {VOICE_NAMES.map((name) => (
            <option key={name} value={name}>
              {name.charAt(0).toUpperCase() + name.slice(1)}
            </option>
          ))}
        </select>
      </label>

      <Segment
        label={t('voice.set.speed')}
        value={settings.speed}
        options={VOICE_SPEEDS.map((speed) => ({ key: speed, label: `${speed}×` }))}
        onSelect={(speed) => onChange({ speed })}
      />
      <Segment
        label={t('voice.set.tone')}
        value={settings.tone}
        options={VOICE_TONES.map((tone) => ({ key: tone, label: tr(`voice.tone.${tone}`) }))}
        onSelect={(tone) => onChange({ tone })}
      />
      <Segment
        label={t('voice.set.model')}
        value={settings.model}
        options={[
          { key: 'fast' as const, label: t('voice.model.fast') },
          { key: 'chat' as const, label: t('voice.model.chat') },
        ]}
        onSelect={(model) => onChange({ model })}
      />
      <Segment
        label={t('voice.set.recognition')}
        value={settings.recognition}
        options={[
          { key: 'standard' as const, label: t('voice.recognition.standard') },
          { key: 'high' as const, label: t('voice.recognition.high') },
        ]}
        onSelect={(recognition) => onChange({ recognition })}
      />
      <Segment
        label={t('voice.set.talkMode')}
        value={settings.talkMode}
        options={VOICE_TALK_MODES.map((mode) => ({ key: mode, label: tr(`voice.talk.${mode}`) }))}
        onSelect={(talkMode) => onChange({ talkMode })}
      />
      {settings.talkMode === 'auto' && (
        <Segment
          label={t('voice.set.reaction')}
          value={settings.reaction}
          options={VOICE_REACTIONS.map((reaction) => ({ key: reaction, label: tr(`voice.reaction.${reaction}`) }))}
          onSelect={(reaction) => onChange({ reaction })}
        />
      )}

      {settings.talkMode === 'auto' && (
        <Toggle
          label={t('voice.set.bargeIn')}
          hint={t('voice.set.bargeInHint')}
          checked={settings.bargeIn}
          onChange={(bargeIn) => onChange({ bargeIn })}
        />
      )}
      <Toggle label={t('voice.set.cues')} checked={settings.cues} onChange={(cues) => onChange({ cues })} />
      {voiceModel?.supportsWebSearch && (
        <Toggle
          label={t('voice.set.webSearch')}
          checked={settings.webSearch}
          onChange={(webSearch) => onChange({ webSearch })}
        />
      )}
    </div>
  );
}
